import ShepButton from "@/components/GenericComponents/ShepButton";
import type { ICustomMatchRow } from "@/features/connections/integrationSlice";
import { 
    Box, 
    Dialog,
    DialogActions,
    DialogContent, 
    DialogTitle, 
    Grid, 
    MenuItem, 
    Select, 
    TextField, 
    Typography, 
    type SelectChangeEvent 
} from "@mui/material";
import { useEffect, useState, type ChangeEvent } from "react";

export const Brackets = {
    None: 0,
    Open: 1,
    Close: 2,
}
export const BracketsDisplay : {
    [key: number]: string;
} = {
    [Brackets.None]: '',
    [Brackets.Open]: '(',
    [Brackets.Close]: ')'
}
export const EquivalencyOptions = {
    NotEqual: 0,
    Equal: 1,
}
export const EquivalencyOptionsDisplay: {
    [key:number]: string;
} = {
    [EquivalencyOptions.Equal]: 'is',
    [EquivalencyOptions.NotEqual]: 'is not',
}

export const ConditionalOptions = {
    None: 0,
    And: 1,
    Or: 2,
}
export const ConditionalOptionsDisplay: {
    [key: string]: string;
} = {
    [ConditionalOptions.None]: '',
    [ConditionalOptions.And]: 'and',
    [ConditionalOptions.Or]: 'or' 
}

interface IEditCustomMatch {
    isOpen: boolean;
    preloadedData: ICustomMatchRow[];
    onClose: () => void;
    setCustomConnectionMatch: (rows: ICustomMatchRow[]) => void;
}
const EditCustomMatch = ({
    isOpen,
    preloadedData,
    onClose,
    setCustomConnectionMatch,
}: IEditCustomMatch) => {
    const [rows, setRows] = useState<ICustomMatchRow[]>(preloadedData);
    const [isValid, setIsValid] = useState<boolean>(true); // make 

    useEffect(() => {
        setRows(preloadedData);
        setIsValid(true);
    }, [setRows, preloadedData, isOpen])

    if (!isOpen) {
        return null;
    }
    const clearForm = () => {
        setRows([]);
    }

    const stringIsValid = (value: string): boolean => {
        return !!value && value.length > 0;
    }

    const positionIsValid = (value: number): boolean => {
        return !isNaN(value) && value > 0;
    }

    const validateForm = () => {
        var nestingLevel = 0;
        const validationResult = () => {
            return rows.reduce((accumulator, row) => {
                if (row.Bracket === Brackets.Open) {
                    nestingLevel += 1;
                }
                if (row.Bracket === Brackets.Close) {
                    nestingLevel -= 1;
                }
                if (nestingLevel < 0) {
                    return false;
                }
                return (
                    accumulator
                    && (row.RowId == 0 || row.Conditional != ConditionalOptions.None)
                    && stringIsValid(row.ElementName)
                    && positionIsValid(row.Position)
                    && stringIsValid(row.Value)
                )
            }, true) && nestingLevel === 0;
        }
        const isValid = validationResult();
        setIsValid(isValid)
        if (isValid) {
            setCustomConnectionMatch(rows);
            onClose();
        }
        return validationResult;
    }
    const newCondition = () => {
        setRows(rows.concat({
            RowId: rows.length,
            Conditional: rows.length === 0 ? ConditionalOptions.None : ConditionalOptions.And,
            Bracket: 0,
            ElementName: "",
            Position: 1,
            Equivalency: 1,
            Value: ''
        }))
    }

    const handleMatchFieldChangeString = (id: number, key: string, value: string) => {
        const newRows = rows.map(row => {
            if (row.RowId === id) {
                return {
                    ...row,
                    [key]: value,
                }
            }
            else return {...row}
        });
        setRows(newRows);
    }

    const handleMatchFieldChangeNumber = (id: number, key: string, value: number) => {
        const newRows = rows.map(row => {
            if (row.RowId === id) {
                return {
                    ...row,
                    [key]: value,
                }
            }
            else return {...row}
        });
        setRows(newRows);
    }

    const matchField = (row: ICustomMatchRow) => {
        return (
            <>
                <Grid item xs={1}>
                    {
                        row.RowId != 0 && (
                            <Select
                                name="Conditional"
                                value={row.Conditional}
                                onChange={(e: SelectChangeEvent<number>) => handleMatchFieldChangeNumber(row.RowId, "Conditional", Number(e.target.value))}
                                sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                                MenuProps={{ 
                                    style: { 
                                        zIndex: 1900,
                                    },
                                }}
                            >
                                {
                                    Object.values(ConditionalOptions).map(option => {
                                        return (
                                            <MenuItem key={option} value={option}>
                                                {ConditionalOptionsDisplay[option]}
                                            </MenuItem>
                                        );
                                    })
                                }
                            </Select>
                        )
                    }
                </Grid>
                <Grid item xs={1}>
                    {
                        <Select
                            name="Bracket"
                            value={row.Bracket === Brackets.Close ? Brackets.None : row.Bracket}
                            onChange={(e: SelectChangeEvent<number>) => handleMatchFieldChangeNumber(row.RowId, "Bracket", Number(e.target.value))}
                            sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                            MenuProps={{ style: { zIndex: 1900 } }}
                        >
                            {
                                Object.values(Brackets).filter(option => option != Brackets.Close).map(option => {
                                    return (
                                        <MenuItem key={option} value={option}>
                                            {BracketsDisplay[option]}
                                        </MenuItem>
                                    );
                                })
                            }
                        </Select>
                    }
                </Grid>
                <Grid item xs={1}>
                    <TextField
                        variant="outlined"
                        helperText="Segment"
                        name="Segment"
                        value={row.ElementName ?? ""}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleMatchFieldChangeString(row.RowId, "ElementName", e.target.value)}
                        sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                    />
                </Grid>
                <Grid item xs={1}>
                    <TextField
                        variant="outlined"
                        helperText="Position"
                        name="Position"
                        type="number"
                        value={row.Position ?? ""}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleMatchFieldChangeString(row.RowId, "Position", e.target.value)}
                        sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Select
                        name="Equivalency"
                        value={row.Equivalency}
                        onChange={(e: SelectChangeEvent<number>) => handleMatchFieldChangeNumber(row.RowId, "Equivalency", Number(e.target.value))}
                        sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                        MenuProps={{ style: { zIndex: 1900 } }}
                    >
                        {
                            Object.values(EquivalencyOptions).map(option => {
                                return (
                                    <MenuItem key={option} value={option}>
                                        {EquivalencyOptionsDisplay[option]}
                                    </MenuItem>
                                );
                            })
                        }
                    </Select>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        variant="outlined"
                        helperText="Value"
                        name="Value"
                        value={row.Value ?? ""}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleMatchFieldChangeString(row.RowId, "Value", e.target.value)}
                        sx={{ width: '100%', marginTop: 1, marginBottom: 2 }}
                    />
                </Grid>
                <Grid item xs={1}>
                    {
                        <Select
                            name="Bracket"
                            disabled={row.Bracket === Brackets.Open}
                            value={row.Bracket === Brackets.Open ? Brackets.None : row.Bracket}
                            onChange={(e: SelectChangeEvent<number>) => handleMatchFieldChangeNumber(row.RowId, "Bracket", Number(e.target.value))}
                            sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
                            MenuProps={{ style: { zIndex: 1900 } }}
                        >
                            {
                                Object.values(Brackets).filter(option => option != Brackets.Open).map(option => {
                                    return (
                                        <MenuItem key={option} value={option}>
                                            {BracketsDisplay[option]}
                                        </MenuItem>
                                    );
                                })
                            }
                        </Select>
                    }
                </Grid>
            </>
        );
    }

    const removeLastRow = () => {
        const lastRowIndex = rows.length;
        const newRows = rows.slice(0, lastRowIndex - 1);
        setRows(newRows);
    }

    const connectionMatchForm = () => { 
        return (
            <Box sx={{
                width: "1000px",
                padding: "50px",
                overflowX: "hidden"
            }}>
                <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={{ xs: 0, sm: 0, md: 0 }}
                    sx={{
                        justifyContent: "center",
                        width: '1000px'
                    }}
                >
                    {
                        !isValid && (
                            <>
                                <Grid item xs={1}/>
                                <Grid item xs={10}> 
                                    <Typography variant="h3">The match rules specified are invalid, verify all fields are specified and brackets are set.</Typography>
                                </Grid>
                                <Grid item xs={1} />
                            </>
                        )
                    }
                    { rows.length > 0 && rows.map(row => matchField(row)) }
                </Grid>
            </Box>
        )
    }
    const handleClose = () => {
        onClose();
    }

    return (
        <>
            <Dialog
                maxWidth={"lg"}
                fullWidth={true}
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        backgroundColor: "var(--disabled)",
                        boxShadow: "none",
                        height: "800px",
                    },
                }}
                sx={{zIndex: 1500}}
            >
                <DialogTitle className="flex-center-aligned">
                    <Typography variant="h2">Configure conditions for custom connection matching</Typography>
                </DialogTitle>
                <DialogContent className="flex-center-aligned" sx={{height: "1100px", overflowY: "auto"}}>
                    {connectionMatchForm()}
                </DialogContent>
                <DialogActions>
                    <div
                        style={{ marginBottom: "30px" }}
                    >
                        <ShepButton variant="secondary" onClick={removeLastRow} sx={{ margin: '5px'}} >
                            Remove Last Row
                        </ShepButton>
                        <ShepButton variant="secondary" onClick={newCondition} sx={{ margin: '5px'}} >
                            Add
                        </ShepButton>
                        <ShepButton variant="secondary" onClick={clearForm} sx={{ margin: '5px'}} >
                            Clear
                        </ShepButton>
                        <ShepButton variant="primary" onClick={validateForm} sx={{ margin: '5px'}} >
                            Accept
                        </ShepButton>
                        <ShepButton variant="primary" onClick={onClose} sx={{ margin: '5px'}} >
                            Close
                        </ShepButton>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    )

}

export default EditCustomMatch;